import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import LazyLoad from 'react-lazyload';

import RenderRichText from 'utils/richText/renderRichText';
import CTAButton from 'utils/richText/functions/CTAButton';
import Embed from 'utils/richText/functions/Embed/index';

import ArrowButton from 'components/buttons/ArrowButton';
import Card from 'components/card';
import CardListLink from 'components/slices/layout/cardList';
import CarouselGallery from 'components/galleries/carouselGallery';
import FormSlice from 'components/newsletterForm';
import Gallery from 'components/galleries/modalGallery';
import InsetImage from 'components/slices/layout/insetImage';
import InformationSection from 'components/airfield/InformationSection';
import TeamCard from 'components/slices/layout/teamCard';
import Table from 'components/tables/table';
import FuelSurchargeTable from 'components/fuelSurcharge/FuelSurchargeTable';
import FuelSurchargeText from 'components/fuelSurcharge/FuelSurchargeText';
import Button from 'components/buttons/Main';

import { H3 } from 'styled';
import { CardListWrap } from 'components/slices/layout/cardList/styled';
import { StyledArticleOverviewWrapper, StyledInsetIcons, StyledFeaturedInsetImgWrapper } from 'components/slices/styled';
import { FullWidthCardContainer } from 'components/grid/styled';

const LoadableSingleMarkerMap = loadable(() => import(/* webpackChunkName: "gatsby-SingleMarkerMap" */'components/maps/SingleMarkerMap'));

const SlicesWrapper = ({ slices, preview, telephoneNumber, props, language, blogAritclePhoneNumber }) => {  
  return (
    <React.Fragment>
      {slices ? 
        slices.map((slice, index) => {
          const sliceType = slice.slice_type;
          
          return (
            <React.Fragment key={slice.id}>
              {(() => {
                switch (sliceType) {     
                  case 'airfield_information':
                    const sectionHeading = slice.primary.section_heading;
                    const columns = slice.items;
                    
                    return (
                      <React.Fragment>
                        <InformationSection 
                          columns={columns} 
                          sectionHeading={sectionHeading}
                          preview={preview}
                        />
                      </React.Fragment>
                    )

                  case 'overview_section':
                  case 'overview___heading_text':
                  case 'heading___overview_text':
                    let overviewSectionText =[];
 
                    if(sliceType === "overview_section") {
                      overviewSectionText = preview ? slice.primary.secion_text : slice.primary.secion_text.raw;
                    } else {
                      overviewSectionText = preview ? slice.primary.overview_text : slice.primary.overview_text.raw;
                    }

                    return (
                      <StyledArticleOverviewWrapper>
                        <RenderRichText richTextArray={overviewSectionText} />
                      </StyledArticleOverviewWrapper>
                    )  

                  case 'featured_50-50_text___img':
                  case 'featured_50-50_text_and_image_':
                    const featuredHeadingText = preview ? slice.primary.featured_headline_text : slice.primary.featured_headline_text.raw;
                    const featuredRichtext = preview ? slice.primary.richtext : slice.primary.richtext.raw;
                    const featuredImg = slice.primary.featured_image;
                    const imagePosition = slice?.primary?.align_image === false && 'left' || 'right';

                    const headingComponent = (
                      <StyledArticleOverviewWrapper> 
                        <RenderRichText richTextArray={featuredHeadingText} /> 
                      </StyledArticleOverviewWrapper>
                    );
                    
                    const iconsComponent = slice.items && slice.items.map(item => {
                      return (
                        <StyledInsetIcons key={item.featured_section_icons.url}>
                          <img src={item.featured_section_icons.url} alt={item.featured_section_icons.alt} />
                          <RenderRichText richTextArray={preview ? item.icon_text : item.icon_text.raw} />
                        </StyledInsetIcons>
                      )  
                    });

                    const richTextComponent = (
                      <div className="richtext">
                        <RenderRichText richTextArray={featuredRichtext} />
                      </div>
                    );

                    const finalCardTextComponent = (
                      <StyledFeaturedInsetImgWrapper>
                        { headingComponent }
                        { iconsComponent && <div style={{margin: '38px 0px'}}> {iconsComponent} </div> }
                        { richTextComponent }
                      </StyledFeaturedInsetImgWrapper>
                    )

                    return (
                      <Card 
                        cardText={finalCardTextComponent}
                        cardImageUrl={featuredImg.url}
                        imageAltText={featuredImg.alt}
                        hasSeperator={false}
                        desktopOneRowCard={true}
                        alignImage={imagePosition}
                        isJetCardProduct={true}
                        margin='50px 0px 70px 0px'
                        objectFitCover={true}
                      />
                    )

                    case 'fxair_card_section': 
                    return (
                      <FullWidthCardContainer marginBottom="40px">
                        {
                          slice.items.map(el => {
                            const cardText = preview ? el.card_text : el.card_text.raw;
                            const cardImageUrl = el.card_image.url;
                            const imagePosition = el.image_position;
                            const zoomButtonText = el.zoom_button_text;
                            const zoomOption = el.zoom_option;
                            const fxAirLogoUrl = el.fxair_logo_url.url;

                            return (
                              <Card 
                                cardText={<RenderRichText richTextArray={cardText} />}
                                headingText={null}
                                cardImageUrl={cardImageUrl}
                                hasSeperator={true}
                                desktopOneRowCard={true}
                                alignImage={imagePosition}
                                zoomOption={zoomOption}
                                zoomButton={Button}
                                zoomButtonText={zoomButtonText}
                                isJetCardProduct={false}
                                isFxAirAnnouncementSlice={true}
                                fxAirLogoUrl={fxAirLogoUrl}
                              />
                            )
                          })
                        }
                      </FullWidthCardContainer>
                    )

                  case 'popular_location_cards':
                    if (slice?.items?.length){
                      const heading = preview ? slice.primary.heading[0].text : slice.primary.heading.text;
                      
                      return (
                        <React.Fragment>
                          <H3 fontSize="24px" style={{margin: '50px 0px 20px 0px'}}> 
                            { heading } 
                          </H3>

                          <div style={{display: 'flex', flexFlow: 'wrap', justifyContent: 'space-between'}}>

                            { slice.items.map(item => {
                                const cardTextComponent = <RenderRichText richTextArray={preview ? item.popular_locations_text : item.popular_locations_text.raw} />;
                                const cardImg = item?.popular_locations_image?.url;
                                const buttonText = item?.popular_locations_button_text;
                                const buttonLink = item?.popular_locations_button_link?.url;

                                return (
                                  <Card 
                                    buttonComponent={
                                      <ArrowButton 
                                        isLink={true}
                                        theme="blue"
                                        buttonText={buttonText}
                                        buttonHref={buttonLink}
                                        language={language}
                                      />
                                    }
                                    buttonAlign="right"
                                    additionalButtonStyle={{marginTop: '0px'}}
                                    textAlign="left"
                                    cardText={cardTextComponent}
                                    cardImageUrl={cardImg}
                                    textMarginBotttom={"15px"}
                                    objectFitCover={true}
                                    isCountryCards={true}
                                    margin="0px 0px 30px 0px"
                                  />
                                )
                              })
                            }
                          
                          </div>  

                        </React.Fragment>
                      )
                    }
                    break;

                  case 'html_table': 
                    const table = !preview ? slice.primary.table.text : slice.primary.table[0].text

                    return <div dangerouslySetInnerHTML={{__html: table}} />

                  case 'single_marker_map': 
                    return (
                      <LazyLoad
                        height={200}
                        once
                        offset={-50}
                      >
                        <LoadableSingleMarkerMap 
                          slice={slice} 
                          zoom={slice.primary.zoom} 
                          destinationLocationData={slice.primary.single_coords}
                        />
                      </LazyLoad>
                    )
                    
                  case 'form':
                    return (
                      <FormSlice 
                        slice={slice} 
                        preview={preview}
                        language={language}
                      />
                    )

                  case 'richtext': 
                    return (
                      <React.Fragment>
                        <RenderRichText 
                          richTextArray={slice.primary.text.raw ? slice.primary.text.raw : slice.primary.text} 
                          telephoneNumber={telephoneNumber}
                          blogAritclePhoneNumber={blogAritclePhoneNumber}
                        />
                      </React.Fragment>
                    )

                  case "anchor":
                  case "anchors":
                    return (
                      <a 
                        id={slice.primary.anchor} 
                        style={{ position: 'absolute', marginTop: '-70px' }}
                      />
                    )

                  case "calltoaction_button": 
                    const alignButton = slice?.primary?.align_button;
                    const isFullWidthButton = slice?.primary?.full_width_button || false;

                    return (
                      <section style={{paddingBottom:'1em', textAlign:`${alignButton || 'center'}`}}>
                        <CTAButton
                          language={language}
                          element={slice.primary.cta_button}
                          slice={slice}
                          url={slice.primary.cta_button !== null && slice.primary.cta_button.url}
                          content={slice.primary.button_name}
                          isFullWidthButton={isFullWidthButton}
                        />
                      </section>
                    )
                  
                  case "gallery_slice":
                    if (slice.primary.gallery_type == 'Carousel'){
                      return <CarouselGallery images={slice.items} />

                    } else {
                      return (
                        <Gallery
                          gallery={[slice]}
                          title={props ? props.gallery_title.text 
                            : [slice].primary && slice.primary.gallery_heading && slice.primary.gallery_heading.text}
                        />
                      )
                    }
                      
                  case "table_2_col":
                    return <Table data={slice} preview={preview} />

                  case "inset_image":
                    return (
                      <InsetImage 
                        data={slice} 
                        preview={preview}
                        telephoneNumber={telephoneNumber}
                        language={language}
                      />
                    )

                  case "youtube_start_end_":
                    return <LazyLoad> <Embed element={slice.items} slice={true} /> </LazyLoad>

                  case "team_card":
                    return <TeamCard data={slice} preview={preview} />

                  // case "campaign_section":
                  //   return (
                  //     <Campaign 
                  //       data={slice} 
                  //       preview={preview}
                  //       phoneNumber={telephoneNumber}
                  //       language={language}
                  //     />
                  //   )

                  case "list_of_articles":
                    return (
                      <CardListWrap>
                        {slice.items.map(item => {
                          if (item.articles_to_link) {
                            if (item.articles_to_link.document) {
                              return (
                                <CardListLink 
                                  article={item.articles_to_link.document[0]} 
                                  preview={preview} 
                                  language={language}
                                />
                              )
                            }
                          }            
                        })}
                      </CardListWrap>
                    )

                  case "fuel_surcharge_table":
                    return <FuelSurchargeTable data={slice} />

                  case "fuel_surcharge_bullet_points":
                    return <FuelSurchargeText data={slice} />

                  default: 
                    return <React.Fragment />
                }
              })()}
            </React.Fragment>
          )
        })
      : null}
    </React.Fragment>
  )
};

SlicesWrapper.propTypes = {
  blogAritclePhoneNumber: PropTypes.string,
  gallery_title: PropTypes.object,
  language: PropTypes.string,
  preview: PropTypes.bool,
  props: PropTypes.object,
  slices: PropTypes.array,
  telephoneNumber: PropTypes.string
};

export default SlicesWrapper;

